'use client';

import type { TFunction } from 'i18next';
import {
  type AnimationEvent,
  type FC,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonClose } from '@/components/buttons';
import { TooltipTypes, useTooltipStore } from '@/store/tooltipStore';

import { ScTooltipGlobal } from './styled';

function isFunction(value: any): value is (t: TFunction) => string {
  return typeof value === 'function';
}

export const GlobalTooltip: FC = () => {
  const { t } = useTranslation();

  const tooltip = useTooltipStore.use.tooltip();
  const remove = useTooltipStore.use.remove();
  const isPaused = !!tooltip?.pauseOnHover;

  useEffect(() => {
    if (!tooltip?.animate || isPaused) return;

    const timeout = setTimeout(() => {
      remove();
    }, tooltip.removeTimeout);

    return () => {
      clearTimeout(timeout);
    };
  }, [tooltip, remove, isPaused]);

  const handleAnimationEnd = useCallback(
    (event: AnimationEvent<HTMLDivElement>) => {
      if (!isPaused || event.target !== event.currentTarget) return;

      remove();
    },
    [isPaused, remove]
  );

  const body = useMemo(() => {
    if (tooltip?.translate) {
      if (isFunction(tooltip.body)) {
        return tooltip.body(t);
      }
      if (typeof tooltip.body === 'string') {
        return t(tooltip.body);
      }
    } else {
      return tooltip?.body;
    }
  }, [tooltip, t]);

  if (tooltip == null) return null;

  const iconClassName = {
    [TooltipTypes.SUCCESS]: 'success-icon',
    [TooltipTypes.INFO]: 'info-icon',
    [TooltipTypes.WARNING]: 'warning-icon',
    [TooltipTypes.ERROR]: 'error-icon',
  }[tooltip.type];

  return (
    <ScTooltipGlobal
      animate={!!tooltip?.animate}
      error={tooltip.type === TooltipTypes.ERROR}
      info={tooltip.type === TooltipTypes.INFO}
      key={tooltip?.id}
      onAnimationEnd={handleAnimationEnd}
      onClick={remove}
      pauseOnHover={isPaused}
      removeTimeout={tooltip?.removeTimeout || 0}
      success={tooltip.type === TooltipTypes.SUCCESS}
      warning={tooltip?.type === TooltipTypes.WARNING}
    >
      <ButtonClose gray onClick={remove} right={0} top={0} />
      {iconClassName && <div className={`icon ${iconClassName}`} />}
      {body}
    </ScTooltipGlobal>
  );
};
